/**
 * Canvas State Guard
 *
 * Defense-in-depth sanitizer for `canvas_state` before we send it to the
 * Supabase save RPC. The RPC also rejects inline base64 images and oversized
 * payloads, but stripping them client-side means the user sees a graceful
 * warning instead of a server-side save failure.
 */

import type { CanvasElement, CanvasState, ImageElement, SvgElement } from '../types';

const MAX_CANVAS_STATE_BYTES = 2 * 1024 * 1024;

export interface CanvasStateGuardResult {
  state: CanvasState;
  removedImageSrc: number;
  removedSvgImages: number;
  sizeBytes: number;
  oversize: boolean;
}

const DATA_URI_IMAGE_RE = /<image\b[^>]*\b(?:xlink:)?href\s*=\s*"data:[^"]*"[^>]*\/?>/gi;

function stripDataHrefFromSvg(markup: string): string {
  if (!markup || !/data:image\//i.test(markup)) return markup;
  return markup.replace(DATA_URI_IMAGE_RE, '');
}

/**
 * Returns a sanitized copy of `state` with any `data:` image payloads
 * removed. Tracks how many were stripped so the UI can warn the user.
 */
export function guardCanvasStateForSave(state: CanvasState): CanvasStateGuardResult {
  let removedImageSrc = 0;
  let removedSvgImages = 0;

  const cleanedElements: CanvasElement[] = state.elements.map((el) => {
    if (el.type === 'image') {
      const img = el as ImageElement;
      if (typeof img.src === 'string' && img.src.startsWith('data:')) {
        removedImageSrc++;
        return { ...img, src: '' } as ImageElement;
      }
      return img;
    }
    if (el.type === 'svg') {
      const svg = el as SvgElement;
      if (svg.svgMarkup && /data:image\//i.test(svg.svgMarkup)) {
        const before = svg.svgMarkup;
        const after = stripDataHrefFromSvg(before);
        if (after !== before) {
          const matches = before.match(DATA_URI_IMAGE_RE);
          removedSvgImages += matches ? matches.length : 1;
          return { ...svg, svgMarkup: after } as SvgElement;
        }
      }
      return svg;
    }
    return el;
  });

  const cleanedState: CanvasState = { ...state, elements: cleanedElements };
  const serialized = JSON.stringify(cleanedState);
  const sizeBytes = new Blob([serialized]).size;

  return {
    state: cleanedState,
    removedImageSrc,
    removedSvgImages,
    sizeBytes,
    oversize: sizeBytes > MAX_CANVAS_STATE_BYTES,
  };
}

export { MAX_CANVAS_STATE_BYTES };
